import { Result, err, ok } from 'neverthrow';
import { z } from 'zod';
import {
  FEATURE_FLAG_OVERRIDE_STORE_VERSION,
  FeatureFlagNames,
  FeatureFlagOverride,
  FeatureFlagOverrideEntry,
  FeatureFlagOverrideStore,
  FeatureFlagValueKind,
  SetFeatureFlagOverrideInput,
  isBooleanFeatureFlag,
  isJsonFeatureFlag,
  isNumberFeatureFlag,
  isStringFeatureFlag,
} from './featureFlagOverrideTypes';

export const FEATURE_FLAG_OVERRIDE_STORAGE_KEY = 'mLoopFeatureFlagOverrideStore';

/**
 * Expected failures when parsing the persisted override store from untrusted localStorage. Absence
 * or emptiness is a successful empty store, not a failure; only structurally broken data fails.
 */
export type FeatureFlagStoreParseFailure =
  | { readonly type: 'MALFORMED_STORE' }
  | { readonly type: 'UNSUPPORTED_VERSION'; readonly version: unknown };

/**
 * Expected failures when setting an override for an untrusted flag key/value pair. UNKNOWN_FLAG
 * means the key is not a registered flag of the requested kind; WRONG_VALUE_TYPE means the key is
 * valid but the value's runtime type does not match.
 */
export type FeatureFlagOverrideFailure =
  | { readonly type: 'UNKNOWN_FLAG'; readonly key: string; readonly expected: FeatureFlagValueKind }
  | {
      readonly type: 'WRONG_VALUE_TYPE';
      readonly key: string;
      readonly expected: FeatureFlagValueKind;
    };

type ParseOptions = {
  now?: Date;
};

const EMPTY_STORE: FeatureFlagOverrideStore = {
  version: FEATURE_FLAG_OVERRIDE_STORE_VERSION,
  entries: [],
};

const booleanSchema = z.boolean();
const numberSchema = z.number();
const optionalStringSchema = z.string().optional();
const rawFeatureFlagRecordSchema = z.record(z.string(), z.unknown());
const stringSchema = z.string();

type RawFeatureFlagRecord = z.infer<typeof rawFeatureFlagRecordSchema>;

const isValidIsoDateTime = (value: string): boolean => !Number.isNaN(Date.parse(value));

const parseOverride = (rawOverride: RawFeatureFlagRecord): FeatureFlagOverride | null => {
  const { type, key, value } = rawOverride;
  const parsedType = stringSchema.safeParse(type);
  const parsedKey = stringSchema.safeParse(key);
  if (!parsedType.success || !parsedKey.success) {
    return null;
  }

  switch (parsedType.data) {
    case 'BOOLEAN': {
      if (!isBooleanFeatureFlag(parsedKey.data)) {
        return null;
      }
      {
        const parsedValue = booleanSchema.safeParse(value);
        return parsedValue.success ? { type: parsedType.data, key: parsedKey.data, value: parsedValue.data } : null;
      }
    }
    case 'JSON':
      if (!isJsonFeatureFlag(parsedKey.data)) {
        return null;
      }
      return { type: parsedType.data, key: parsedKey.data, value };
    case 'NUMBER': {
      if (!isNumberFeatureFlag(parsedKey.data)) {
        return null;
      }
      {
        const parsedValue = numberSchema.safeParse(value);
        return parsedValue.success ? { type: parsedType.data, key: parsedKey.data, value: parsedValue.data } : null;
      }
    }
    case 'STRING': {
      if (!isStringFeatureFlag(parsedKey.data)) {
        return null;
      }
      {
        const parsedValue = stringSchema.safeParse(value);
        return parsedValue.success ? { type: parsedType.data, key: parsedKey.data, value: parsedValue.data } : null;
      }
    }
    default:
      return null;
  }
};

const parseEntry = (rawEntry: RawFeatureFlagRecord, now: Date): FeatureFlagOverrideEntry | null => {
  const parsedOverride = rawFeatureFlagRecordSchema.safeParse(rawEntry.override);
  if (!parsedOverride.success) {
    return null;
  }
  const override = parseOverride(parsedOverride.data);
  if (!override) {
    return null;
  }

  const { source, reason, createdAt, expiresAt } = rawEntry;
  if (source !== 'DEVTOOLS' && source !== 'AGENT' && source !== 'TEST' && source !== 'PROFILE') {
    return null;
  }
  const parsedCreatedAt = stringSchema.safeParse(createdAt);
  if (!parsedCreatedAt.success || !isValidIsoDateTime(parsedCreatedAt.data)) {
    return null;
  }
  const parsedReason = optionalStringSchema.safeParse(reason);
  if (!parsedReason.success) {
    return null;
  }
  const parsedExpiresAt = optionalStringSchema.safeParse(expiresAt);
  if (!parsedExpiresAt.success) {
    return null;
  }

  if (parsedExpiresAt.data !== undefined) {
    if (!isValidIsoDateTime(parsedExpiresAt.data)) {
      return null;
    }
    if (Date.parse(parsedExpiresAt.data) <= now.getTime()) {
      return null;
    }
  }

  const entry: FeatureFlagOverrideEntry = {
    override,
    source,
    createdAt: parsedCreatedAt.data,
  };
  if (parsedReason.data) {
    entry.reason = parsedReason.data;
  }
  if (parsedExpiresAt.data !== undefined) {
    entry.expiresAt = parsedExpiresAt.data;
  }
  return entry;
};

// The JSON.parse boundary is the only throwing step; fromThrowable folds a malformed-JSON throw
// into the typed MALFORMED_STORE failure so the rest of the parse composes on a Result.
const parseStoreJson = Result.fromThrowable(
  (raw: string) => rawFeatureFlagRecordSchema.safeParse(JSON.parse(raw)),
  (): FeatureFlagStoreParseFailure => ({ type: 'MALFORMED_STORE' })
);

/**
 * Parses the persisted override store, surfacing structural problems as typed failures instead of
 * silently collapsing to an empty store. Absent/empty input is a successful empty store. Individual
 * invalid or expired entries are still dropped from the ok value — entry expiry is normal behavior,
 * not a store-level failure.
 */
export const parseFeatureFlagOverrideStore = (
  rawStore: string | null | undefined,
  options: ParseOptions = {}
): Result<FeatureFlagOverrideStore, FeatureFlagStoreParseFailure> => {
  if (!rawStore) {
    return ok(EMPTY_STORE);
  }

  return parseStoreJson(rawStore).andThen((parsed): Result<FeatureFlagOverrideStore, FeatureFlagStoreParseFailure> => {
    if (!parsed.success) {
      return err({ type: 'MALFORMED_STORE' });
    }
    const parsedStore = parsed.data;
    if (parsedStore.version !== FEATURE_FLAG_OVERRIDE_STORE_VERSION) {
      return err({ type: 'UNSUPPORTED_VERSION', version: parsedStore.version });
    }
    if (!Array.isArray(parsedStore.entries)) {
      return err({ type: 'MALFORMED_STORE' });
    }

    const now = options.now || new Date();
    return ok({
      version: FEATURE_FLAG_OVERRIDE_STORE_VERSION,
      entries: parsedStore.entries.flatMap((entry) => {
        const parsedEntryRecord = rawFeatureFlagRecordSchema.safeParse(entry);
        if (!parsedEntryRecord.success) {
          return [];
        }
        const parsedEntry = parseEntry(parsedEntryRecord.data, now);
        return parsedEntry ? [parsedEntry] : [];
      }),
    });
  });
};

/**
 * Reads and parses the persisted override store, collapsing any parse failure to the empty store.
 * This is the single visible place the silent fallback lives, instead of being baked into the parser.
 */
export const readFeatureFlagOverrideStore = (): FeatureFlagOverrideStore => {
  if (!('localStorage' in globalThis)) {
    return EMPTY_STORE;
  }
  return parseFeatureFlagOverrideStore(globalThis.localStorage.getItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY)).unwrapOr(
    EMPTY_STORE
  );
};

export const writeFeatureFlagOverrideStore = (store: FeatureFlagOverrideStore) => {
  if (!('localStorage' in globalThis)) {
    return;
  }
  globalThis.localStorage.setItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY, JSON.stringify(store));
};

// Splits the previously conflated validity check: an unregistered key for the requested kind is an
// UNKNOWN_FLAG, while a registered key carrying the wrong runtime value type is a WRONG_VALUE_TYPE.
const toOverride = ({
  key,
  type,
  value,
}: SetFeatureFlagOverrideInput): Result<FeatureFlagOverride, FeatureFlagOverrideFailure> => {
  switch (type) {
    case 'BOOLEAN': {
      if (!isBooleanFeatureFlag(key)) {
        return err({ type: 'UNKNOWN_FLAG', key, expected: 'BOOLEAN' });
      }
      const parsedValue = booleanSchema.safeParse(value);
      if (!parsedValue.success) {
        return err({ type: 'WRONG_VALUE_TYPE', key, expected: 'BOOLEAN' });
      }
      return ok({ type, key, value: parsedValue.data });
    }
    case 'JSON':
      if (!isJsonFeatureFlag(key)) {
        return err({ type: 'UNKNOWN_FLAG', key, expected: 'JSON' });
      }
      return ok({ type, key, value });
    case 'NUMBER': {
      if (!isNumberFeatureFlag(key)) {
        return err({ type: 'UNKNOWN_FLAG', key, expected: 'NUMBER' });
      }
      const parsedValue = numberSchema.safeParse(value);
      if (!parsedValue.success) {
        return err({ type: 'WRONG_VALUE_TYPE', key, expected: 'NUMBER' });
      }
      return ok({ type, key, value: parsedValue.data });
    }
    case 'STRING': {
      if (!isStringFeatureFlag(key)) {
        return err({ type: 'UNKNOWN_FLAG', key, expected: 'STRING' });
      }
      const parsedValue = stringSchema.safeParse(value);
      if (!parsedValue.success) {
        return err({ type: 'WRONG_VALUE_TYPE', key, expected: 'STRING' });
      }
      return ok({ type, key, value: parsedValue.data });
    }
    default: {
      const exhaustiveCheck: never = type;
      return exhaustiveCheck;
    }
  }
};

/**
 * Persists an override for an untrusted flag input, returning the next store on success or a typed
 * validation failure instead of throwing. Callers (e.g. the dev API) must check the Result.
 */
export const setFeatureFlagOverride = (
  input: SetFeatureFlagOverrideInput
): Result<FeatureFlagOverrideStore, FeatureFlagOverrideFailure> => {
  return toOverride(input).map((override) => {
    const now = new Date();
    const existingStore = readFeatureFlagOverrideStore();
    const entry: FeatureFlagOverrideEntry = {
      override,
      source: input.source,
      createdAt: now.toISOString(),
    };
    if (input.reason) {
      entry.reason = input.reason;
    }
    if (input.ttlMinutes) {
      entry.expiresAt = new Date(now.getTime() + input.ttlMinutes * 60 * 1000).toISOString();
    }

    const nextStore: FeatureFlagOverrideStore = {
      version: FEATURE_FLAG_OVERRIDE_STORE_VERSION,
      entries: [...existingStore.entries.filter((existing) => existing.override.key !== input.key), entry],
    };
    writeFeatureFlagOverrideStore(nextStore);
    return nextStore;
  });
};

export const clearFeatureFlagOverride = (key: FeatureFlagNames): FeatureFlagOverrideStore => {
  const existingStore = readFeatureFlagOverrideStore();
  const nextStore: FeatureFlagOverrideStore = {
    version: FEATURE_FLAG_OVERRIDE_STORE_VERSION,
    entries: existingStore.entries.filter((entry) => entry.override.key !== key),
  };
  writeFeatureFlagOverrideStore(nextStore);
  return nextStore;
};

export const clearFeatureFlagOverrides = (): FeatureFlagOverrideStore => {
  writeFeatureFlagOverrideStore(EMPTY_STORE);
  return EMPTY_STORE;
};
